import { Injectable } from '@angular/core';
import { mergeMap as _observableMergeMap, catchError as _observableCatch } from 'rxjs/operators';
import { Observable, throwError as _observableThrow, of as _observableOf } from 'rxjs';
import { HttpHeaders } from "@angular/common/http";
import {
    AuthorisedStatesNames,
    CalculateAllResult,
    ComposeHtmlContentMetadata,
    ComposeHtmlContentMetadataResult,
    FindSearchControlsResponse,
    GetMobileVerificationStatusResult,
    GetProductDetailsResponse,
    GetProductEntitlementsResponse,
    GetProductListResponse,
    GetProductMenuResponse,
    GetProductResponse,
    GetProductSectionsCountResponse,
    PingResponse,
    SendMobileVerificationCodeResult,
    StartMobileVerificationTimerResult,
    SystemSettingsResult,
    UploadFileResponse,
    ValidatorListResult,
    VerifyMobileVerificationCodeResult,
} from "./model/form-service.model";
import { VaultProductsFilter, GetVaultProductResult, ProductSearchResult } from "./model/form-vault.model";
import { ProductDefinitionArray } from "app/shared/model";
import { ServiceProxyBase } from './_service-proxy-base';

@Injectable({ providedIn: 'root' })
export class FormServiceProxy extends ServiceProxyBase {
    startMobileVerificationTimer(
        applicationId: string,
        controlName: string,
        dataPath: string): Observable<never | StartMobileVerificationTimerResult | null> {

        let url_ = (this.baseUrl + "/StartPhoneVerificationTimer").replace(/[?&]$/, "");

        let content_ = {
            "applicationId": applicationId,
            "controlName": controlName,
            "dataPath": dataPath
        };

        let options_ = this.createPostOptions(content_);

        return this.createHttpObserver(StartMobileVerificationTimerResult, "post", url_, options_);
    }

    checkProductVersions(productIdList: string[]): Observable<any> {
        let url_ = this.baseUrl + "/CheckProductVersions";
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            body: JSON.stringify({
                products: productIdList
            }),
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_);
    }

    syncOffline(): Observable<any> {
        let url_ = this.baseUrl + "/SyncOffline";
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            body: JSON.stringify({}),
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain",
                "ngsw-bypass": "true"
            })
        };

        return this.http.request("post", url_, options_);
    }

    getMobileVerificationStatus(
        applicationId: string,
        controlName: string,
        dataPath: string): Observable<GetMobileVerificationStatusResult | never | null> {
        let url_ = (this.baseUrl + "/GetPhoneVerificationStatus").replace(/[?&]$/, "");

        let content_ = {
            "applicationId": applicationId,
            "controlName": controlName,
            "dataPath": dataPath
        };

        let options_ = this.createPostOptions(content_);

        return this.createHttpObserver(GetMobileVerificationStatusResult, "post", url_, options_);
    }

    verifyMobileVerificationCode(
        applicationId: string,
        controlName: string,
        dataPath: string,
        code: string): Observable<VerifyMobileVerificationCodeResult | never | null> {

        let url_ = (this.baseUrl + "/VerifyTwoFactorCode").replace(/[?&]$/, "");

        let content_ = {
            "applicationId": applicationId,
            "controlName": controlName,
            "dataPath": dataPath,
            "code": code
        };

        let options_ = this.createPostOptions(content_);

        return this.createHttpObserver(VerifyMobileVerificationCodeResult, "post", url_, options_);
    }

    sendMobileVerificationCode(
        applicationId: string,
        controlName: string,
        dataPath: string,
        phoneNumber: string): Observable<SendMobileVerificationCodeResult | never | null> {

        let url_ = (this.baseUrl + "/SendVerificationCode").replace(/[?&]$/, "");

        let content_ = {
            "applicationId": applicationId,
            "controlName": controlName,
            "dataPath": dataPath,
            "phoneNumber": phoneNumber
        };

        let options_ = this.createPostOptions(content_);

        return this.createHttpObserver(SendMobileVerificationCodeResult, "post", url_, options_);
    }

    getComposeHtmlContent(
        formId: string,
        applicationId: string,
        data: string,
        versionNumber: number | null,
        controlsData: ComposeHtmlContentMetadata[]): Observable<ComposeHtmlContentMetadataResult | never | null> {

        let url_ = (this.baseUrl + "/ComposeHtmlContent").replace(/[?&]$/, "");
        let content_ = {
            "formId": formId,
            "versionNumber": versionNumber,
            "applicationId": applicationId,
            "data": data,
            "controlsData": controlsData
        };

        let options_ = this.createPostOptions(content_);

        return this.createHttpObserver(ComposeHtmlContentMetadataResult, "post", url_, options_);
    }

    calculateAll(
        formId: string,
        formVersion: number,
        useFormVersion: boolean,
        applicationId: string,
        data: string,
        pageId: number
    ): Observable<CalculateAllResult | never | null> {
        let url_ = (this.baseUrl + "/CalculateAll").replace(/[?&]$/, "");

        let content_ = {
            "formId": formId,
            "formVersion": formVersion,
            "useFormVersion": useFormVersion,
            "applicationId": applicationId,
            "data": JSON.stringify(data),
            "pageId": pageId
        };

        let options_ = this.createPostOptions(content_);

        return this.createHttpObserver(CalculateAllResult, "post", url_, options_);
    }

    getValidatorList(syncVersion: number): Observable<ValidatorListResult | never | null> {
        let url_ = (this.baseUrl + "/GetValidatorList").replace(/[?&]$/, "");

        let content_ = { "syncVersion": syncVersion };
        let options_ = this.createPostOptions(content_);

        return this.createHttpObserver(ValidatorListResult, "post", url_, options_);
    }

    getSystemSettings(): Observable<SystemSettingsResult | never | null> {
        let url_ = (this.baseUrl + "/GetSystemSettings").replace(/[?&]$/, "");

        let options_: any = {
            observe: "response",
            responseType: "json",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.createHttpObserver(SystemSettingsResult, "post", url_, options_);
    }

    getAuthorisedStates(formId: string): Observable<void | object> {
        let url_ = (this.baseUrl + "/GetAuthorisedStates").replace(/[?&]$/, "");

        let content_ = { "formId": formId };
        let options_ = this.createPostOptions(content_);

        return this.createHttpObserver(AuthorisedStatesNames, "post", url_, options_);
    }

    getProductList(): Observable<void | GetProductListResponse> {
        let url_ = (this.dashboardUrl + "/GetProductList").replace(/[?&]$/, "");

        let options_: any = {
            observe: "response",
            responseType: "json",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.createHttpObserver(GetProductListResponse, "post", url_, options_);
    }

    /**
     * Pings this instance.
     * @return Success
     */
    ping(): Observable<void | PingResponse> {
        let url_ = (this.baseUrl + "/Ping").replace(/[?&]$/, "");
        let options_: any = {
            observe: "response",
            responseType: "json",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain",
                "ngsw-bypass": "true"
            })
        };

        return this.createHttpObserver(PingResponse, "post", url_, options_);
    }

    findVaultProducts(data: VaultProductsFilter | undefined): Observable<ProductSearchResult> {
        let url_ = (this.baseUrl + "/FindVaultProducts").replace(/[?&]$/, "");
        let options_ = this.createPostOptions(data);
        return this.createHttpObserver(ProductSearchResult, "post", url_, options_);
    }

    /**
     * Retrieves a single form vault product (normally for storage offline).
     * @param productId The product ID to retrieve.
     * @returns A Form Vault Product, with categories and base URL.
     */
    getVaultProduct(productId: string): Observable<GetVaultProductResult> {
        let url_ = (this.baseUrl + "/GetVaultProduct").replace(/[?&]$/, "");
        let options_ = this.createPostOptions({ productId: productId });
        return this.createHttpObserver(GetVaultProductResult, "post", url_, options_);
    }

    getProduct(formId: string, versionNumber: number | null, pageId: number, applicationId: string): Observable<GetProductResponse> {
        let url_ = (this.baseUrl + "/GetProduct").replace(/[?&]$/, "");
        let options_ = this.createPostOptions({
            "id": formId,
            "versionNumber": versionNumber,
            "pageId": pageId,
            "applicationId": applicationId
        });

        return this.createHttpObserver(GetProductResponse, "post", url_, options_);
    }

    getProductMenu(formId: string,
        applicationId: string | undefined,
        formVersion: number | undefined): Observable<GetProductMenuResponse> {
        let url_ = (this.baseUrl + "/GetProductMenu").replace(/[?&]$/, "");

        let options_ = this.createPostOptions({
            formId: formId,
            applicationId: applicationId || '',
            formVersion: formVersion ?? null
        });

        return this.createHttpObserver(GetProductMenuResponse, "post", url_, options_);
    }

    getProductEntitlements(formId: string, formVersion: number | null, pageId: number | null, applicationId: string)
        : Observable<GetProductEntitlementsResponse> {
        let url_ = (this.baseUrl + "/GetAccessRights").replace(/[?&]$/, "");
        let options_ = this.createPostOptions({
            formId: formId,
            formVersion: formVersion,
            pageId: pageId,
            applicationId: applicationId
        });

        return this.createHttpObserver(GetProductEntitlementsResponse, "post", url_, options_);
    }

    getProducts(organisationId: string): Observable<void | ProductDefinitionArray> {
        let url_ = (this.baseUrl + "/GetProducts").replace(/[?&]$/, "");
        let options_ = this.createPostOptions({ "organisationId": organisationId });
        return this.createHttpObserver(ProductDefinitionArray, "post", url_, options_);
    }

    getProductSectionsCount(formId: string, versionNumber: number | null, applicationId: string | null): Observable<void | GetProductSectionsCountResponse> {
        let url_ = (this.baseUrl + "/GetProductSectionsCount").replace(/[?&]$/, "");
        let options_ = this.createPostOptions({
                id: formId,
                versionNumber: versionNumber,
                applicationId: applicationId
            });

        return this.createHttpObserver(GetProductSectionsCountResponse, "post", url_, options_);
    }

    getProductDetails(formId: string): Observable<void | GetProductDetailsResponse> {
        let url_ = (this.baseUrl + "/GetProductDetails").replace(/[?&]$/, "");
        let options_ = this.createPostOptions({ id: formId });
        return this.createHttpObserver(GetProductDetailsResponse, "post", url_, options_);
    }

    uploadFile(data: FormData): Observable<void | UploadFileResponse> {
        let url_ = this.baseUploadUrl;
        url_ = url_.replace(/[?&]$/, '');

        let options_: any = {
            body: data,
            observe: "response",
            responseType: "json",
            headers: new HttpHeaders({
                "Accept": 'application/json, text/javascript, */*; q=0.01',
                // "Content-Type": "application/json",
            })
        };

        return this.createHttpObserver(UploadFileResponse, "post", url_, options_);
    }

    findSearchControls(formId: string): Observable<void | FindSearchControlsResponse> {
        let url_ = (this.dashboardUrl + "/FindSearchControls").replace(/[?&]$/, "");
        let options_ = this.createPostOptions({ formId: formId });
        return this.createHttpObserver(FindSearchControlsResponse, "post", url_, options_);
    }
}
